import { ChefHat, CircleAlert, Copy } from "lucide-react";
import { Link, Navigate, useSearchParams } from "react-router";
import { toast } from "sonner";

import { routeTo } from "@/app/routes";

import { useSession } from "@/core/auth/session";
import { useBrand, type Brand } from "@/core/brand/brand-context";
import { formatCount, formatCountOf, formatDay, formatDayLong, todayIn } from "@/core/format/format";
import { useDocumentTitle } from "@/core/hooks/use-document-title";
import { log } from "@/core/log";
import { Button } from "@/ui/button";
import { Card, CardHeader } from "@/ui/card";
import { cn } from "@/ui/cn";
import { Skeleton } from "@/ui/spinner";
import { Stat } from "@/ui/stat";
import { ErrorState, PageHeader } from "@/ui/states";
import { Tabs } from "@/ui/tabs";

import { useAnalyticsSummary, type SignupRange } from "./analytics.queries";
import { useReportSummary, type ReportSummary } from "./home.queries";
import {
  DayView,
  KeyNumbers,
  MonthView,
  PlansView,
  RecentOrdersCard,
  RetentionCard,
  SignupsCard,
} from "./Overview";
import { isMonth, SIGNUP_RANGES } from "./overview-display";

function greeting(name: string | null | undefined): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

/**
 * What the brand's status means for its customers, when it isn't simply live (`setup`: the extra line for
 * people who can set up the catalogue).
 */
const STATUS_NOTICE: Partial<
  Record<Brand["status"], { title: string; body: string; setup?: string; tone: "warn" | "danger" }>
> = {
  DRAFT: {
    title: "Not live yet",
    body: "Customers can't buy your plans yet. The Brocoly team makes your brand live when it's ready.",
    setup: "Set up your dishes, plans and menus first.",
    tone: "warn",
  },
  SUSPENDED: {
    title: "Paused by the Brocoly team",
    body: "Customers can't buy new plans. Plans already bought carry on as usual. Contact the Brocoly team to find out why.",
    tone: "danger",
  },
  CLOSED: {
    title: "Closed",
    body: "This brand no longer takes customers.",
    tone: "danger",
  },
};

function StatusNotice({ brand, canSetUp }: { brand: Brand; canSetUp: boolean }) {
  const notice = STATUS_NOTICE[brand.status];
  if (!notice) return null;
  return (
    <div
      role="status"
      className={cn("flex gap-3 rounded-2xl p-4", notice.tone === "warn" ? "bg-warn-soft" : "bg-danger-soft")}
    >
      <CircleAlert
        className={cn("mt-0.5 size-5 shrink-0", notice.tone === "warn" ? "text-warn" : "text-danger")}
        aria-hidden="true"
      />
      <div className="flex flex-col gap-1">
        <span className="font-bold">{notice.title}</span>
        <span className="text-sm text-ink-2">
          {notice.body}
          {canSetUp && notice.setup && ` ${notice.setup}`}
        </span>
      </div>
    </div>
  );
}

/** How customers reach the brand: the join code to share, or that the app lists it. */
function JoinCodeCard({ brand }: { brand: Brand }) {
  if (brand.visibility === "PUBLIC") {
    return (
      <Card>
        <CardHeader title="How customers find you" className="mb-1" />
        <p className="text-sm text-ink-2">Anyone can find {brand.name} in the Brocoly app.</p>
      </Card>
    );
  }
  if (!brand.joinCode) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(brand.joinCode!);
      toast.success("Code copied");
    } catch (error) {
      log.warn("Couldn't copy the join code", error);
      toast.error("Couldn't copy. Select the code and copy it yourself.");
    }
  };
  return (
    <Card className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-[17px] font-bold">Your join code</h2>
        <p className="text-sm text-muted">Customers enter it in the Brocoly app to see your plans.</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="tabular rounded-xl bg-surface-muted px-4 py-2 font-mono text-xl font-extrabold tracking-[0.2em] select-all">
          {brand.joinCode}
        </span>
        <Button variant="secondary" size="icon" aria-label="Copy the join code" onClick={() => void onCopy()}>
          <Copy aria-hidden="true" />
        </Button>
      </div>
    </Card>
  );
}

/** The last 30 days' things to keep an eye on, and the rating. */
function WorthALook({ data }: { data: ReportSummary }) {
  const period = `${formatDay(data.from)} – ${formatDay(data.to)}`;
  const watch = [
    { label: "Disputes opened", value: data.disputesOpened, note: "Customers said a meal never arrived" },
    {
      label: "Missed by your kitchen",
      value: data.meals.missedByBrand,
      note: "Each one moved to the end of its plan",
    },
    { label: "Bonus meals", value: data.meals.bonus, note: "Added free, at the end of a plan" },
  ];
  return (
    <Card aria-labelledby="watch">
      <CardHeader id="watch" title="Worth a look" aside={period} />
      <ul className="flex flex-col">
        {watch.map((w) => (
          <li
            key={w.label}
            className="flex items-center gap-3 border-t border-border-subtle py-3 first:border-t-0 first:pt-0"
          >
            <span className="flex min-w-0 grow flex-col gap-0.5">
              <span className="text-sm font-semibold">{w.label}</span>
              <span className="text-[13px] text-muted">{w.note}</span>
            </span>
            <span className="tabular text-lg font-extrabold">{formatCount(w.value)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

type View = "today" | "month" | "plans";
const VIEWS: readonly View[] = ["today", "month", "plans"];

/** Which view, day, month and chart period are open, kept in the address so a reload keeps them. */
function useOverviewChoice(today: string) {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.includes(params.get("view") as View) ? (params.get("view") as View) : "today";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") ?? "") ? params.get("date")! : today;
  const monthParam = params.get("month");
  const month = isMonth(monthParam) && monthParam <= today.slice(0, 7) ? monthParam : today.slice(0, 7);
  const rangeParam = params.get("range");
  const range = SIGNUP_RANGES.some((r) => r.value === rangeParam) ? (rangeParam as SignupRange) : "1m";
  const set = (changes: Record<string, string | undefined>) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  return {
    view,
    date,
    month,
    range,
    setView: (next: View) => set({ view: next === "today" ? undefined : next }),
    setDate: (day: string) => set({ date: day === today ? undefined : day }),
    setMonth: (m: string) => set({ month: m === today.slice(0, 7) ? undefined : m }),
    setRange: (r: SignupRange) => set({ range: r === "1m" ? undefined : r }),
    openDay: (day: string) => set({ view: undefined, date: day === today ? undefined : day }),
  };
}

export function HomePage() {
  const { me } = useSession();
  const { brand, can } = useBrand();
  useDocumentTitle(brand.name);
  const today = todayIn(brand.timezone);
  const choice = useOverviewChoice(today);
  const report = useReportSummary(brand.id, can("reports"));
  const summary = useAnalyticsSummary(brand.id, choice.range, can("reports"), choice.month);

  // Staff work from the orders; this page has nothing else for them.
  if (!can("reports")) return <Navigate to={routeTo.orders(brand.id)} replace />;

  const rating = report.data?.ratings;
  return (
    <>
      <PageHeader
        title={greeting(me?.name)}
        subtitle={formatDayLong(today)}
        actions={
          <Button asChild variant="secondary">
            <Link to={routeTo.orders(brand.id)}>
              <ChefHat aria-hidden="true" />
              Today's orders
            </Link>
          </Button>
        }
      />
      <StatusNotice brand={brand} canSetUp={can("catalogue")} />
      {summary.isPending ? (
        <div
          className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-6"
          aria-busy="true"
          aria-label="Loading"
        >
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[112px] rounded-2xl" />
          ))}
        </div>
      ) : summary.isError ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : (
        <KeyNumbers
          summary={summary.data}
          thisMonth={today.slice(0, 7)}
          onMonth={choice.setMonth}
          extra={
            <Stat
              label="Rating"
              value={rating && rating.overallCount > 0 ? rating.overallAverage.toFixed(1) : "–"}
              detail={
                rating && rating.overallCount > 0
                  ? formatCountOf(rating.overallCount, "review")
                  : "No reviews yet"
              }
            />
          }
        />
      )}

      <Tabs
        label="Overview"
        value={choice.view}
        onChange={choice.setView}
        items={[
          {
            value: "today",
            label: "Today",
            content: <DayView date={choice.date} today={today} onDate={choice.setDate} />,
          },
          {
            value: "month",
            label: "Monthly",
            content: (
              <MonthView
                month={choice.month}
                today={today}
                onMonth={choice.setMonth}
                onDay={choice.openDay}
              />
            ),
          },
          { value: "plans", label: "Plans", content: <PlansView /> },
        ]}
      />

      {summary.data && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <SignupsCard summary={summary.data} today={today} range={choice.range} onRange={choice.setRange} />
          <RetentionCard summary={summary.data} />
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <RecentOrdersCard />
        <div className="flex flex-col gap-4">
          {report.data && <WorthALook data={report.data} />}
          <JoinCodeCard brand={brand} />
        </div>
      </div>
    </>
  );
}
